import React, { useCallback, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import AudioPlayerBar, {
  type AudioPlayerBarTrack,
} from "../../../components/audio-player-bar/AudioPlayerBar";
import { CommunicationAudioPlayer } from "../../../components/audio-preview";
import { CircularAudioGallery } from "../../../components/circular-audio-gallery";
import DropdownMenu from "../../../components/dropdown-menu/DropdownMenu";
import FilterChipBar from "../../../components/filter-chip/FilterChipBar";
import MediaPreview from "../../../components/media-preview/MediaPreview";
import MediaTranscriptionModal from "../../../components/media-preview/components/MediaTranscriptionModal";
import useCopyToClipboard from "../../../hooks/useCopyToClipboard";
import type { DeviceState, MediaPreviewHandle } from "../../../types/media-preview";
import type {
  ProfileMediaLayout,
  ProfileWork,
  WorkFilterTag,
} from "../../../types/profile";

export interface ProfileMediaSectionProps {
  works: ProfileWork[];
  /**
   * 作品フィルタータグのマスタ（position 順）。割当作品が 1 件以上あるタグを
   * バー表示する。未設定ならバーなし（ハッシュタグは GALLERY 検索用メタデータで
   * あり、グループ分けには使わない）。
   */
  filterTags?: WorkFilterTag[];
  /** プロフィール所有者の表示名（再生バーのクリエイター表示に使う）。 */
  creatorName?: string;
  /** プロフィール所有者の slug（再生バーからのプロフィール遷移に使う）。 */
  creatorSlug?: string;
  /**
   * 作品展示方法（既定 thumbnail）。circle / list は音源作品のみが対象で、
   * 音源以外（画像 / 動画 / PDF / YouTube）は従来のサムネイルグリッドで併記する。
   */
  layout?: ProfileMediaLayout;
}

/**
 * ホーム完了タブ（`AllShotsList`）の `CompletedShotsListWrapper` と同じレイアウト。
 * gap はモバイル 8px / PC 16px。
 */
const WorksWrapper = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${(props) => (props.$isMobile ? "8px" : "16px")};
  width: 100%;
`;

/**
 * カード幅はサムネの自然幅（アスペクト比）に追従させる。完了タブの
 * `motion.div` + `ShotContainer` 相当（block 要素 → 中身の shrink-to-fit）。
 */
const WorkItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: fit-content;
  max-width: 100%;
`;

const Title = styled.span`
  width: 100%;
  overflow-wrap: anywhere;
`;

/** リスト表示（メッセンジャーのオーディオ表示と同じ波形プレイヤーの縦積み・中央寄せ）。 */
const AudioListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
`;

const AudioListItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

/** リスト行の見出し部（タイトル・メタ + 右端の３点リーダー）。 */
const AudioListHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
`;

const AudioListHeaderTexts = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

/**
 * 共有リンク（?work={id}）の対象作品 ID。公開プロフィール（soremo.jp/{slug}）と
 * 明示ルート（/profile/{slug}）の両方で動くよう location から直接読む。
 */
const readSharedWorkId = (): string | null =>
  new URLSearchParams(window.location.search).get("work");

/**
 * メディアプレビューセクション。
 *
 * 展示方法（`layout`）で表示を切り替える:
 * - thumbnail（既定）: ホーム完了タブと同じサムネ表示。
 * - circle: 音源作品を `CircularAudioGallery`（円形配置。円の中身はサムネと同じ
 *   ジャケット画像 / 生成サムネイル）で表示。
 * - list: 音源作品を `CommunicationAudioPlayer`（Peaks 波形）の縦積み（中央寄せ）で
 *   表示。各行の右端に３点リーダー（共有リンクをコピー）を置く。
 * circle / list では音源以外の作品を従来のサムネイルグリッドで下に併記する。
 *
 * - thumbnail / circle の音源クリックは画面下のフローティングプレイヤーバー
 *   （`AudioPlayerBar`）を開く。`?work={id}` の共有リンク復元も両レイアウトで動く。
 * - 画像 / 動画 / PDF / YouTube は `MediaPreview` のクリックプレビューで全画面モーダルを開く。
 * - YouTube リンクは旧 Django 互換のホバープレビュー再生（mute autoplay）に対応。
 * - フィルタータグ（`work_filter_tags`）が設定されていればそのバーで絞り込み。
 *   「すべて」チップは置かず、先頭のタグを自動選択する（常にいずれかのタグが選択状態）。
 *   未設定ならバーは表示しない（ハッシュタグへのフォールバックはしない）。
 */
const ProfileMediaSection: React.FC<ProfileMediaSectionProps> = ({
  works,
  filterTags,
  creatorName,
  creatorSlug,
  layout = "thumbnail",
}) => {
  // 共有リンクで遷移した場合、対象作品が初期選択タグ（先頭タグ）の絞り込みで
  // 隠れて再生できなくならないよう、対象作品が属するタグ（マスタ position 順で
  // 最初のもの）を初期選択する。
  const [selectedTag, setSelectedTag] = useState<string | null>(() => {
    const workId = readSharedWorkId();
    if (!workId) return null;
    const workTagIds = works.find((w) => String(w.id) === workId)?.filter_tag_ids ?? [];
    return (filterTags ?? []).find((t) => workTagIds.includes(t.id))?.id ?? null;
  });
  // 再生バーの共有リンクからの復元（対象が音源作品ならバーを自動で開く）。
  const [playingId, setPlayingId] = useState<string | null>(readSharedWorkId);
  // 共有リンク（?work=）で開いた回かどうか。共有された人にはまず鑑賞モードで
  // 作品として観てもらう（以降の曲送りでは開かない）。
  const openedFromShareLink = useRef(readSharedWorkId() != null);
  const { isMobile } = useSelector((state: { device: DeviceState }) => state.device);

  // 共有リンク: 現在のプロフィール URL に ?work={id} を載せたディープリンク。
  const buildShareUrl = useCallback((workId: string) => {
    const url = new URL(window.location.href);
    url.search = "";
    url.searchParams.set("work", workId);
    return url.toString();
  }, []);

  // リスト行の３点リーダー用（フローティングプレイヤーバーと同じ文言・挙動）。
  const { copy: copyShareUrl } = useCopyToClipboard({
    successMessage: "共有リンクをコピーしました",
  });

  // 文字起こしモーダルの対象（fileId = AlbumVariation.pk）。
  // list レイアウトでは行プレイヤーへの ref 経由でセグメントクリックのシークを行う。
  const [transcriptionTarget, setTranscriptionTarget] = useState<{
    workId: string;
    fileId: string;
    fileName?: string;
  } | null>(null);
  const listPlayerHandlesRef = useRef(new Map<string, MediaPreviewHandle>());

  // フィルタータグマスタのうち、割当作品が 1 件以上あるもの（マスタ position 順）。
  const barItems = useMemo(
    () =>
      (filterTags ?? [])
        .filter((tag) => works.some((w) => (w.filter_tag_ids ?? []).includes(tag.id)))
        .map((t) => ({ id: t.id, name: t.name })),
    [filterTags, works],
  );

  // 「すべて」チップは置かないため、未選択時は先頭タグを自動選択する。
  const effectiveTag = useMemo(() => {
    if (selectedTag && barItems.some((t) => t.id === selectedTag)) return selectedTag;
    return barItems[0]?.id ?? null;
  }, [selectedTag, barItems]);

  const filtered = useMemo(() => {
    if (!effectiveTag) return works;
    return works.filter((w) => (w.filter_tag_ids ?? []).includes(effectiveTag));
  }, [works, effectiveTag]);

  // circle / list の対象は音源作品のみ。音源以外はサムネイルグリッドで併記する。
  const audioWorks = useMemo(
    () => filtered.filter((w) => w.media[0]?.type === "audio"),
    [filtered],
  );
  const nonAudioWorks = useMemo(
    () => filtered.filter((w) => w.media[0]?.type !== "audio"),
    [filtered],
  );

  // フローティングプレイヤーの再生キュー（表示中リストの音源作品のみ、表示順）。
  const audioTracks = useMemo<AudioPlayerBarTrack[]>(
    () =>
      audioWorks.map((w) => ({
        id: String(w.id),
        title: w.title,
        src: w.media[0].url,
        thumbnailSrc: w.image_url || undefined,
        waveformUrl: w.media[0].waveform_url || undefined,
        creatorName,
        creatorSlug,
        shareUrl: buildShareUrl(String(w.id)),
        subtitleText: w.subtitle_text || undefined,
      })),
    [audioWorks, creatorName, creatorSlug, buildShareUrl],
  );

  // フィルター変更等でキューから外れたらバーを閉じる（index は -1 になる）。
  const playingIndex = playingId ? audioTracks.findIndex((t) => t.id === playingId) : -1;

  if (works.length === 0) return null;

  const renderThumb = (work: ProfileWork) => {
    const primary = work.media[0];
    // ホーム完了タブと同じサムネサイズ（高さ固定・幅はメディアのアスペクト比に追従）。
    const sizeMode = {
      mode: "fixed-height" as const,
      height: isMobile ? "128px" : "192px",
    };

    if (primary && (primary.type === "image" || primary.type === "video")) {
      return (
        <MediaPreview
          src={primary.url}
          mediaType={primary.type}
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          hoverPlay={primary.type === "video" && !isMobile}
          enableClickPreview
          disableHoverEffect
          fileName={primary.file_name}
          fileId={primary.id != null ? String(primary.id) : undefined}
        />
      );
    }

    if (primary && primary.type === "pdf") {
      return (
        <MediaPreview
          src={primary.url}
          mediaType="pdf"
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          enableClickPreview
          disableHoverEffect
          fileName={primary.file_name}
        />
      );
    }

    if (!primary && work.youtube_link) {
      return (
        <MediaPreview
          src={work.youtube_link}
          mediaType="youtube"
          sizeMode={sizeMode}
          hoverPlay={!isMobile}
          enableClickPreview
          disableHoverEffect
        />
      );
    }

    if (!primary && work.image_url) {
      return (
        <MediaPreview src={work.image_url} mediaType="image" sizeMode={sizeMode} enableClickPreview disableHoverEffect />
      );
    }
    if (!primary) return null;

    // 音源: サムネ画像（image_url）があればそれを、なければ音そのものの実測から
    // 組み立てた生成サムネイルを表示する。
    // ホバーでは鳴らさず、クリックで画面下のフローティングプレイヤーバーを開く。
    // バー再生中は画面内のサムネイルがその再生位置に合わせて動き続ける。
    return (
      <div
        style={{ cursor: "pointer" }}
        onClick={() => setPlayingId(String(work.id))}
        role="button"
        aria-label={`${work.title} を再生`}
      >
        <MediaPreview
          src={primary.url}
          mediaType="audio"
          thumbnailSrc={work.image_url || undefined}
          sizeMode={sizeMode}
          disableHoverEffect
          fileName={primary.file_name}
          fileId={primary.id != null ? String(primary.id) : undefined}
          waveformUrl={primary.waveform_url || undefined}
        />
      </div>
    );
  };

  const renderWorkMeta = (work: ProfileWork) =>
    (work.created_year || work.credit) && (
      <span className="bodytext-11 u-text-light-gray u-line-height-150">
        {[work.created_year, work.credit].filter(Boolean).join(" / ")}
      </span>
    );

  const renderWorksGrid = (list: ProfileWork[]) => (
    <WorksWrapper $isMobile={isMobile}>
      {list.map((work) => (
        <WorkItem key={work.id}>
          {renderThumb(work)}
          <Title className="bodytext-13">{work.title}</Title>
          {renderWorkMeta(work)}
        </WorkItem>
      ))}
    </WorksWrapper>
  );

  return (
    <section className="u-w100 u-col u-gap16 u-pt32 u-pb32">
      {barItems.length > 0 && (
        <FilterChipBar
          items={barItems}
          selectedId={effectiveTag}
          onSelect={setSelectedTag}
          showAllChip={false}
        />
      )}

      {layout === "circle" && (
        <>
          {audioWorks.length > 0 && (
            <CircularAudioGallery
              items={audioWorks.map((w) => ({
                id: String(w.id),
                title: w.title,
                src: w.media[0].url,
                thumbnailSrc: w.image_url || undefined,
                waveformUrl: w.media[0].waveform_url || undefined,
                cacheKey: `profile-work-${w.id}`,
              }))}
              onItemClick={(item) => setPlayingId(item.id)}
              style={{ maxWidth: 560, margin: "0 auto" }}
            />
          )}
          {nonAudioWorks.length > 0 && renderWorksGrid(nonAudioWorks)}
        </>
      )}

      {layout === "list" && (
        <>
          {audioWorks.length > 0 && (
            <AudioListWrapper>
              {audioWorks.map((work) => (
                <AudioListItem key={work.id}>
                  <AudioListHeader>
                    <AudioListHeaderTexts>
                      <Title className="bodytext-13">{work.title}</Title>
                      {renderWorkMeta(work)}
                    </AudioListHeaderTexts>
                    <DropdownMenu
                      items={[
                        {
                          label: "共有リンクをコピー",
                          icon: "link",
                          onClick: () => {
                            void copyShareUrl(buildShareUrl(String(work.id)));
                          },
                        },
                        ...(work.media[0].id != null
                          ? [
                              {
                                label: "文字起こし",
                                icon: "speech_to_text",
                                onClick: () =>
                                  setTranscriptionTarget({
                                    workId: String(work.id),
                                    fileId: String(work.media[0].id),
                                    fileName: work.media[0].file_name,
                                  }),
                              },
                            ]
                          : []),
                      ]}
                      placement="bottom-end"
                    />
                  </AudioListHeader>
                  <CommunicationAudioPlayer
                    ref={(handle) => {
                      const key = String(work.id);
                      if (handle) {
                        listPlayerHandlesRef.current.set(key, handle);
                      } else {
                        listPlayerHandlesRef.current.delete(key);
                      }
                    }}
                    src={work.media[0].url}
                    waveformUrl={work.media[0].waveform_url || undefined}
                    cacheKey={`profile-work-${work.id}`}
                    showMenu={false}
                  />
                </AudioListItem>
              ))}
            </AudioListWrapper>
          )}
          {nonAudioWorks.length > 0 && renderWorksGrid(nonAudioWorks)}
        </>
      )}

      {layout === "thumbnail" && renderWorksGrid(filtered)}

      {/* 音源クリック時のフローティングプレイヤーバー（thumbnail / circle）。
          list は行内の波形プレイヤーで再生する。他メディアは MediaPreview
          内蔵モーダルを使うため対象外。 */}
      {layout !== "list" && playingIndex >= 0 && (
        <AudioPlayerBar
          tracks={audioTracks}
          currentIndex={playingIndex}
          onIndexChange={(i) => setPlayingId(audioTracks[i].id)}
          onClose={() => setPlayingId(null)}
          openInAppreciation={openedFromShareLink.current}
        />
      )}

      {/* 文字起こしモーダル（list レイアウトの行メニューから） */}
      {transcriptionTarget && (
        <MediaTranscriptionModal
          fileId={transcriptionTarget.fileId}
          fileName={transcriptionTarget.fileName}
          onClose={() => setTranscriptionTarget(null)}
          onSeek={(seconds) => {
            listPlayerHandlesRef.current
              .get(transcriptionTarget.workId)
              ?.seekTo(seconds);
          }}
        />
      )}
    </section>
  );
};

export default ProfileMediaSection;
